import { useEffect, useRef, useState, useCallback } from 'react';

// Haversine — distance in meters between two lat/lng points
export function haversine(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function formatDistance(m) {
  if (m == null || !isFinite(m)) return '—';
  const miles = m / 1609.34;
  if (miles < 0.1) return `${Math.round(m * 3.281)} ft`;
  if (miles < 10)  return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles)} mi`;
}

/**
 * useGeofencing
 *
 * @param {Object[]} fences  - [{ id, lat, lng, radius?, customerId?, stopId? }]
 * @param {Object}   options
 *   - enabled (bool)             default true
 *   - defaultRadius (meters)     default 100
 *   - prolongedAfterMs (number)  default 45 * 60 * 1000  (45 min)
 *   - onEnter(fence, position)
 *   - onExit(fence, durationMs, position)
 *   - onProlonged(fence, durationMs, position)
 *
 * Returns:
 *   - position: { lat, lng, accuracy } | null
 *   - error: string | null
 *   - permission: 'granted' | 'denied' | 'prompt' | 'unknown'
 *   - distances: Map<fenceId, meters>
 *   - inside: Set<fenceId>
 *   - request(): re-prompt for permission
 *   - stop(): stop watching
 */
export function useGeofencing(fences, options = {}) {
  const {
    enabled = true,
    defaultRadius = 100,
    prolongedAfterMs = 45 * 60 * 1000,
    onEnter,
    onExit,
    onProlonged,
  } = options;

  const [position, setPosition]     = useState(null);
  const [error, setError]           = useState(null);
  const [permission, setPermission] = useState('unknown');
  const [distances, setDistances]   = useState(new Map());
  const [inside, setInside]         = useState(new Set());

  const insideRef       = useRef(new Set());
  const enterTimesRef   = useRef(new Map());
  const prolongedRef    = useRef(new Set());
  const watchIdRef      = useRef(null);
  const fencesRef       = useRef(fences);
  const onEnterRef      = useRef(onEnter);
  const onExitRef       = useRef(onExit);
  const onProlongedRef  = useRef(onProlonged);

  fencesRef.current      = fences;
  onEnterRef.current     = onEnter;
  onExitRef.current      = onExit;
  onProlongedRef.current = onProlonged;

  const processPosition = useCallback((pos) => {
    const here = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
    setPosition(here);

    const currentFences = fencesRef.current || [];
    const newDistances  = new Map();
    const newInside     = new Set();

    for (const f of currentFences) {
      if (f.lat == null || f.lng == null) continue;
      const d = haversine(here, { lat: f.lat, lng: f.lng });
      newDistances.set(f.id, d);
      const radius = f.radius ?? defaultRadius;
      if (d <= radius) newInside.add(f.id);
    }

    setDistances(newDistances);

    // Compare against previous insideRef set
    const prev = insideRef.current;
    const now  = Date.now();

    // Entered
    for (const id of newInside) {
      if (!prev.has(id)) {
        enterTimesRef.current.set(id, now);
        const f = currentFences.find(x => x.id === id);
        if (f && onEnterRef.current) onEnterRef.current(f, here);
      }
    }
    // Exited
    for (const id of prev) {
      if (!newInside.has(id)) {
        const enteredAt = enterTimesRef.current.get(id);
        const duration  = enteredAt ? now - enteredAt : 0;
        enterTimesRef.current.delete(id);
        prolongedRef.current.delete(id);
        const f = currentFences.find(x => x.id === id);
        if (f && onExitRef.current) onExitRef.current(f, duration, here);
      }
    }
    // Prolonged (still inside past threshold, fire once)
    for (const id of newInside) {
      if (prolongedRef.current.has(id)) continue;
      const enteredAt = enterTimesRef.current.get(id);
      if (enteredAt && (now - enteredAt) >= prolongedAfterMs) {
        prolongedRef.current.add(id);
        const f = currentFences.find(x => x.id === id);
        if (f && onProlongedRef.current) onProlongedRef.current(f, now - enteredAt, here);
      }
    }

    insideRef.current = newInside;
    setInside(newInside);
  }, [defaultRadius, prolongedAfterMs]);

  const stop = useCallback(() => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const request = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported by this browser');
      return;
    }
    if (watchIdRef.current != null) return;
    setError(null);
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setPermission('granted');
        processPosition(pos);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setPermission('denied');
        setError(err.message || 'Geolocation error');
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5_000,
        timeout: 15_000,
      }
    );
    watchIdRef.current = id;
  }, [processPosition]);

  // Auto-start when enabled
  useEffect(() => {
    if (!enabled) {
      stop();
      return;
    }
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      return;
    }
    // Check current permission state if available
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' }).then(p => {
        setPermission(p.state);
        if (p.state !== 'denied') request();
      }).catch(() => {
        request();
      });
    } else {
      request();
    }
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Periodic poll for prolonged-stay detection even if position hasn't updated
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => {
      const now = Date.now();
      for (const id of insideRef.current) {
        if (prolongedRef.current.has(id)) continue;
        const enteredAt = enterTimesRef.current.get(id);
        if (enteredAt && (now - enteredAt) >= prolongedAfterMs) {
          prolongedRef.current.add(id);
          const f = (fencesRef.current || []).find(x => x.id === id);
          if (f && onProlongedRef.current) {
            onProlongedRef.current(f, now - enteredAt, position);
          }
        }
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [enabled, prolongedAfterMs, position]);

  return { position, error, permission, distances, inside, request, stop };
}
